import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, BookmarkCheck, Route, Sparkles, Clock } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import SocialLoginButtons from "@/components/SocialLoginButtons";
import { useI18n } from "@/lib/i18n";
import { useAnalytics, ANALYTICS_EVENTS } from "@/hooks/useAnalytics";
import { useGuestSaves } from "@/hooks/useGuestSaves";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatRemaining(ms: number, t: (k: string) => string) {
  const totalMin = Math.floor(ms / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h <= 0) return `${m} ${t("saveGate.minutesShort")}`;
  return `${h} ${t("saveGate.hoursShort")} ${m} ${t("saveGate.minutesShort")}`;
}

const GuestRescueSheet = ({ open, onOpenChange }: Props) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const saves = useGuestSaves();
  const [tick, setTick] = useState(0);

  // 1-minute live ticker for countdown text
  useEffect(() => {
    if (!open) return;
    const id = window.setInterval(() => setTick((x) => x + 1), 60_000);
    return () => window.clearInterval(id);
  }, [open]);

  useEffect(() => {
    if (open) {
      track(ANALYTICS_EVENTS.AUTH_GATE_SHOWN, {
        source: "guest_rescue_sheet",
        item_count: saves.totalCount,
      });
    }
  }, [open, saves.totalCount, track]);

  const remaining = saves.msUntilExpiry ?? 0;
  const isUrgent = remaining > 0 && remaining < 6 * 60 * 60 * 1000;
  void tick; // re-render trigger

  const handleRescue = () => {
    track(ANALYTICS_EVENTS.AUTH_GATE_SIGNUP_CLICK, {
      source: "guest_rescue_sheet",
      item_count: saves.totalCount,
    });
    navigate(`/signup?redirect=${encodeURIComponent("/trips?restore=1")}`);
  };

  if (saves.totalCount === 0) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="p-0 overflow-y-auto rounded-t-3xl border-t-0 max-h-[92vh]"
      >
        <div className="flex justify-center pt-2.5 pb-1">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Urgent banner */}
        <div
          className={`px-6 pt-3 pb-5 ${
            isUrgent
              ? "bg-gradient-to-br from-destructive/15 via-destructive/5 to-transparent"
              : "bg-gradient-to-br from-primary/12 via-primary/4 to-transparent"
          }`}
        >
          <SheetHeader className="text-left space-y-2.5">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 220 }}
              className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                isUrgent ? "bg-destructive/15" : "bg-primary/15"
              }`}
            >
              <AlertTriangle
                className={`h-6 w-6 ${isUrgent ? "text-destructive" : "text-primary"}`}
                strokeWidth={2.4}
              />
            </motion.div>
            <SheetTitle className="font-display text-[22px] leading-tight font-bold text-foreground">
              {t("guestRescue.title").replace("{count}", String(saves.totalCount))}
            </SheetTitle>
            <SheetDescription className="flex items-center gap-1.5 text-[13px] leading-relaxed text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              <span>
                {remaining > 0
                  ? t("guestRescue.expiresIn").replace(
                      "{time}",
                      formatRemaining(remaining, t)
                    )
                  : t("guestRescue.expired")}
              </span>
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="px-6 pb-8 space-y-5">
          {/* Saved items list */}
          <div className="space-y-2">
            {saves.hasPendingTrip && saves.pendingTripTitle && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-accent/40 border border-accent">
                <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
                  <Route className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold uppercase text-primary tracking-wide">
                    {t("guestRescue.trip")}
                  </p>
                  <p className="text-[14px] font-semibold text-foreground truncate">
                    {saves.pendingTripTitle}
                  </p>
                </div>
              </div>
            )}

            {saves.wishlistItems.map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-3 rounded-xl bg-accent/30 border border-accent/60"
              >
                <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
                  <BookmarkCheck className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold uppercase text-muted-foreground tracking-wide">
                    {t("guestRescue.wishlist")}
                  </p>
                  <p className="text-[14px] font-semibold text-foreground truncate">
                    {item.name}
                  </p>
                  {item.city && (
                    <p className="text-[11px] text-muted-foreground truncate">
                      {item.city}
                      {item.country ? `, ${item.country}` : ""}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="space-y-3 pt-1">
            <Button
              onClick={handleRescue}
              size="lg"
              className="w-full h-13 text-[15px] font-bold gap-2 shadow-lg shadow-primary/25"
            >
              <Sparkles className="h-4 w-4" />
              {t("guestRescue.rescueCta")}
            </Button>

            <SocialLoginButtons />
          </div>

          <p className="text-center text-[11px] text-muted-foreground/80">
            {t("guestRescue.footnote")}
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default GuestRescueSheet;
